import * as React from "react"
import type { Map as LeafletMap, Marker as LeafletMarker } from "leaflet"

import type { Property } from "@/lib/types"
import { cn } from "@/lib/utils"

import "leaflet/dist/leaflet.css"

function createPopupContent(property: Property) {
  const link = document.createElement("a")
  link.href = `/property/${encodeURIComponent(property.slug)}`
  link.className = "property-map-popup"

  const image = property.images.at(0)
  if (image) {
    const element = document.createElement("img")
    element.src = image.url
    element.alt = image.alt
    element.loading = "lazy"
    link.appendChild(element)
  }

  const reference = document.createElement("span")
  reference.className = "property-map-popup-reference"
  reference.textContent = property.reference
  link.appendChild(reference)

  const title = document.createElement("strong")
  title.textContent = property.title
  link.appendChild(title)

  const location = document.createElement("span")
  location.textContent = `${property.locality}, ${property.region}`
  link.appendChild(location)

  return link
}

export function PropertyMap({
  properties,
  activeId,
  onActivate,
  openFirstPopup = false,
  className,
}: {
  properties: Property[]
  activeId?: number
  onActivate?: (id: number) => void
  openFirstPopup?: boolean
  className?: string
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<LeafletMap | null>(null)
  const markersRef = React.useRef(new Map<number, LeafletMarker>())
  const onActivateRef = React.useRef(onActivate)
  const [ready, setReady] = React.useState(false)

  React.useEffect(() => {
    onActivateRef.current = onActivate
  }, [onActivate])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container || properties.length === 0) return

    let active = true
    let resizeFrame = 0

    void import("leaflet").then((leaflet) => {
      if (!active || !containerRef.current) return

      const first = properties[0]
      const map = leaflet.map(container, {
        center: [first.latitude, first.longitude],
        zoom: 15,
        scrollWheelZoom: false,
        zoomControl: true,
        // Route transitions can unmount the map while Leaflet is finishing its
        // CSS zoom transition. Static demo maps do not need animated movement,
        // and disabling it prevents a late transitionend from touching a map
        // pane that has already been removed.
        zoomAnimation: false,
        fadeAnimation: false,
        markerZoomAnimation: false,
      })
      mapRef.current = map

      // Browser acceptance is intentionally hermetic. Production keeps the
      // attributed OpenStreetMap layer; browser-test mode renders the same
      // interactive markers on the local fallback surface without third-party
      // tile requests.
      if (import.meta.env.MODE !== "browser-test") {
        leaflet
          .tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
          })
          .addTo(map)
      }

      const icon = leaflet.divIcon({
        className: "property-map-marker",
        html: '<span class="property-map-pin" aria-hidden="true"><span></span></span>',
        iconSize: [34, 42],
        iconAnchor: [17, 40],
        popupAnchor: [0, -36],
      })
      const bounds = leaflet.latLngBounds([])

      for (const property of properties) {
        const marker = leaflet
          .marker([property.latitude, property.longitude], {
            alt: `${property.title} location`,
            icon,
            keyboard: true,
            title: property.title,
          })
          .addTo(map)
          .bindPopup(createPopupContent(property), {
            className: "property-map-popup-shell",
            maxWidth: 320,
            minWidth: 220,
          })

        marker.on("click", () => onActivateRef.current?.(property.id))
        markersRef.current.set(property.id, marker)
        bounds.extend([property.latitude, property.longitude])
      }

      if (properties.length > 1) {
        map.fitBounds(bounds, { maxZoom: 14, padding: [36, 36] })
      } else if (openFirstPopup) {
        markersRef.current.get(first.id)?.openPopup()
      }

      resizeFrame = window.requestAnimationFrame(() => map.invalidateSize())
      setReady(true)
    })

    return () => {
      active = false
      window.cancelAnimationFrame(resizeFrame)
      markersRef.current.clear()
      mapRef.current?.stop()
      mapRef.current?.remove()
      mapRef.current = null
      setReady(false)
    }
  }, [openFirstPopup, properties])

  React.useEffect(() => {
    if (!activeId) return
    const marker = markersRef.current.get(activeId)
    if (!marker) return
    marker.openPopup()
    mapRef.current?.panTo(marker.getLatLng())
  }, [activeId])

  return (
    <div
      className={cn(
        "property-map relative min-h-96 overflow-hidden border border-border bg-muted",
        className
      )}
      data-no-print
    >
      <div
        ref={containerRef}
        className="absolute inset-0"
        role="region"
        aria-label={
          properties.length === 1
            ? `Exact map location for ${properties[0].title}`
            : `Map showing ${properties.length} property locations`
        }
      />
      {!ready ? (
        <div className="absolute inset-0 grid place-items-center bg-muted text-sm text-muted-foreground">
          Loading exact property locations…
        </div>
      ) : null}
    </div>
  )
}
